<template>
  <div
    class="flex min-h-screen w-full flex-col items-center justify-center px-4"
  >
    <KekkleCard shadow="purple" class="w-full max-w-md text-center">
      <!-- Loading state -->
      <div v-if="state === 'loading'">
        <div class="mb-4 flex justify-center">
          <div
            class="h-12 w-12 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"
          />
        </div>
        <h1 class="text-xl font-bold dark:text-slate-200">
          {{ $t("pages.invitemembersfollowup.searching") }}
        </h1>
      </div>

      <!-- Success state -->
      <div v-else-if="state === 'success'">
        <p class="mb-2 text-4xl">🎉</p>
        <h1 class="mb-2 text-xl font-bold dark:text-slate-200">
          {{ $t("pages.invitemembersfollowup.now_a_members") }}
        </h1>
        <p class="text-sm text-gray-500 dark:text-slate-400">
          {{ $t("pages.invitemembersfollowup.redirecting") }}
        </p>
      </div>

      <!-- Info state (already member, group full, not found) -->
      <div v-else-if="state === 'info'">
        <p class="mb-2 text-4xl">{{ infoIcon }}</p>
        <h1 class="mb-2 text-xl font-bold dark:text-slate-200">
          {{ infoMessage }}
        </h1>
        <p class="text-sm text-gray-500 dark:text-slate-400">
          {{ $t("pages.invitemembersfollowup.redirecting") }}
        </p>
      </div>

      <!-- Error state -->
      <div v-else-if="state === 'error'">
        <InformationText
          color="red"
          :information="$t('pages.invitemembersfollowup.joining_failed')"
        ></InformationText>
        <p class="mt-3 text-sm text-gray-500 dark:text-slate-400">
          {{ $t("pages.invitemembersfollowup.redirecting") }}
        </p>
      </div>
    </KekkleCard>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { httpsCallable } from "firebase/functions";
import { getIdToken, getIdTokenResult } from "firebase/auth";
import { firebaseFunctions } from "@/helpers/firebase/firebase";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useNotificationsStore } from "@/stores/modules/notifications/notifications";
import KekkleCard from "@/components/ui/KekkleCard.vue";
import InformationText from "@/components/InformationText.vue";

type PageState = "loading" | "success" | "info" | "error";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const authStore = useAuthStore();
const userStore = useUserStore();
const groupsStore = useGroupsStore();
const notificationsStore = useNotificationsStore();

const state = ref<PageState>("loading");
const infoMessage = ref("");
const infoIcon = ref("ℹ️");

function redirectToDashboard() {
  setTimeout(() => {
    router.push({ name: "Group" });
  }, 2500);
}

async function joinGroup(token: string) {
  try {
    const joinFunction = httpsCallable(firebaseFunctions, "joinGroup");
    await joinFunction({ token: token.toUpperCase() });
    authStore.refreshAuthIdToken();
    notificationsStore.setHasFetched(false);
    state.value = "success";
    await userStore.refreshUserData();
    await groupsStore.refreshAllUserGroupsData();
    redirectToDashboard();
  } catch (e: unknown) {
    const error = e as { message?: string; code?: string };
    if (error.message === "Group Full") {
      infoIcon.value = "😅";
      infoMessage.value = t("pages.invitemembersfollowup.group_full");
      state.value = "info";
    } else if (error.message === "Group does not exist") {
      infoIcon.value = "🤷";
      infoMessage.value = t("pages.invitemembersfollowup.does_not_exist");
      state.value = "info";
    } else if (error.code === "functions/unavailable") {
      infoIcon.value = "👋";
      infoMessage.value = t("pages.invitemembersfollowup.already_a_members");
      state.value = "info";
    } else {
      state.value = "error";
    }
    redirectToDashboard();
  }
}

onMounted(() => {
  // Read token from query first, then fall back to localStorage
  console.log("In the mounted");
  const token =
    (route.query.token as string | undefined) ||
    window.localStorage.getItem("joinToken") ||
    "";

  // Clear localStorage after reading
  window.localStorage.removeItem("joinToken");

  if (!token) {
    console.log("hi");
    router.push({ name: "Group" });
  } else {
    console.log("Hiiiii", token);
    joinGroup(token);
  }
});
</script>
